import clsx from "clsx";

// Interfaces
import { IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";

// Helpers
import { getPayPeriodParts } from "@/Washouts/helpers/SplitPeriods";

// MUI
import {
	Typography,
	Dialog,
	DialogTitle,
	IconButton,
	DialogContent,
	DialogActions,
	Grid,
	FormControlLabel,
	Checkbox,
	Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// Custom Components
import PartnerPeriodsChecks from "../PartnerPeriodsChecks";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

interface IPositionSelectorDialogProps {
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	openPositionsSelector: boolean;
	closePositionsSelector: () => void;
	uniqPositionStatuses?: IStatus[];
	checkedAll: boolean;
	handleCheckAll: (value: boolean) => void;
	selectedPositions: string[];
	selectedPayPeriodPart: string[];
	disabledPositions: string[] | undefined;
	handleChangeSelectedPositions: (position?: string) => void;
	handleChangePayPeriodPart: (payPeriodPart?: string) => void;
	handlePositiveActionButton: () => void;
	titleDialog: string;
	emptyPositionsMessage?: string;
	positiveActionButtonText?: string;
	commissionCycleId?: string;
}

const PositionSelectorDialog = (props: IPositionSelectorDialogProps) => {
	const {
		openPositionsSelector,
		closePositionsSelector,
		uniqPositionStatuses,
		checkedAll,
		handleCheckAll,
		selectedPositions,
		selectedPayPeriodPart,
		handleChangePayPeriodPart,
		disabledPositions,
		handleChangeSelectedPositions,
		handlePositiveActionButton,
		titleDialog,
		emptyPositionsMessage,
		positiveActionButtonText,
		payPeriod,
		partnerPayPeriod,
		commissionCycleId,
	} = props;

	const payPeriodParts = getPayPeriodParts({ commissionCycle: { _id: commissionCycleId ?? "" }, payPeriod, partnerPayPeriod });
	const isReviewDialog = positiveActionButtonText === "Review";
	const isButtonDisabled = !uniqPositionStatuses?.length || !selectedPositions.length;
	return (
		<BootstrapDialog open={openPositionsSelector} disableEscapeKeyDown onClose={closePositionsSelector}>
			<DialogTitle>
				<Typography className={"regular-montserrat-label fs-24"} sx={{ lineHeight: "1.2" }}>
					{titleDialog}
				</Typography>
				<IconButton
					aria-label="close"
					onClick={closePositionsSelector}
					sx={{
						position: "absolute",
						right: 5,
						top: 5,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ maxWidth: "520px", minWidth: "520px" }} className="dialog-content-p25-vert">
				<Grid item xs={12} sx={{ paddingTop: "20px" }}>
					{uniqPositionStatuses?.length ? (
						<Grid container direction={"column"}>
							{!!partnerPayPeriod && !isReviewDialog && (
								<PartnerPeriodsChecks
									payPeriodParts={payPeriodParts}
									selections={selectedPayPeriodPart}
									isDisabled={!uniqPositionStatuses?.length}
									onChangeAction={(value) => handleChangePayPeriodPart(value)}
								/>
							)}
							<Grid item>
								<FormControlLabel
									control={
										<Checkbox
											checked={checkedAll}
											onChange={(_event, value) => handleCheckAll(value)}
											disabled={!uniqPositionStatuses?.length}
										/>
									}
									label="Select All"
									labelPlacement="end"
									className="group-pay-plan-assign-container-select-all"
								/>
							</Grid>
							<Grid item sx={{ paddingTop: "10px" }}>
								<Grid container>
									{uniqPositionStatuses?.map((position, i) => {
										return (
											<Grid item key={`${position.position}_${i}`} xs={6}>
												<Grid container direction={"row"}>
													<Grid item>
														<FormControlLabel
															control={
																<Checkbox
																	checked={!!selectedPositions.find((item) => item === position.position)}
																	onChange={() => handleChangeSelectedPositions(position.position)}
																/>
															}
															disabled={disabledPositions?.includes(position.position)}
															label={position.position}
															labelPlacement="end"
															className="payroll-submission-position"
														/>
													</Grid>
												</Grid>
											</Grid>
										);
									})}
								</Grid>
							</Grid>
						</Grid>
					) : (
						<Typography>{emptyPositionsMessage}</Typography>
					)}
				</Grid>
			</DialogContent>
			<DialogActions className="dialog-actions-p25-p20">
				<Grid container direction="row" justifyContent="flex-end" spacing={2} sx={{ padding: "0 10px 10px" }}>
					<Grid item>
						<Paper elevation={0}>
							<Button
								variant="contained"
								classes={
									"!bg-gray-blue h-[30px] rounded-[6px] !shadow-none hover:!shadow-none !font-sans !capitalize !text-white !text-[14px]"
								}
								text={"Cancel"}
								sx={{ minWidth: "80px", minHeight: "30px" }}
								onClick={closePositionsSelector}
							/>
						</Paper>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							classes={clsx({
								"h-[30px] !shadow-none !font-sans !capitalize !text-white !text-[14px]": true,
								"!bg-primary hover:!shadow-primary-shadow-50": !isButtonDisabled,
								"!bg-disabled !cursor-default !shadow-none hover:!shadow-none": isButtonDisabled,
							})}
							text={positiveActionButtonText ?? ""}
							disabled={isButtonDisabled}
							sx={{ minWidth: "80px", minHeight: "30px" }}
							onClick={handlePositiveActionButton}
						/>
					</Grid>
				</Grid>
			</DialogActions>
		</BootstrapDialog>
	);
};

export default PositionSelectorDialog;
